import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSnackbar } from 'notistack5';
import moment from 'moment-timezone';
import { saveAs } from 'file-saver';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  IconButton,
  Button,
  Grid,
  Paper,
  Skeleton,
  Tooltip,
  TableSortLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Collapse,
  Alert,
  Badge,
  Avatar,
  useMediaQuery,
  useTheme,
  Popover,
  Modal,
  Divider
} from '@material-ui/core';
import { visuallyHidden } from '@material-ui/utils';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import filterFill from '@iconify/icons-eva/funnel-fill';
import downloadFill from '@iconify/icons-eva/download-fill';
import calendarFill from '@iconify/icons-eva/calendar-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import personFill from '@iconify/icons-eva/person-fill';
import shieldFill from '@iconify/icons-eva/shield-fill';
import activityFill from '@iconify/icons-eva/activity-fill';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import arrowUpFill from '@iconify/icons-eva/arrow-up-fill';
import arrowDownFill from '@iconify/icons-eva/arrow-down-fill';

import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import usersAPI from '../utils/api/users';
import storage from '../utils/storage';

// Enhanced table head configuration
const TABLE_HEAD = [
  { id: 'timestamp', label: 'Timestamp', alignRight: false, sortable: true },
  { id: 'user', label: 'User', alignRight: false, sortable: true },
  { id: 'role', label: 'Role', alignRight: false, sortable: true },
  { id: 'action', label: 'Activity', alignRight: false, sortable: true },
  { id: 'category', label: 'Category', alignRight: false, sortable: true },
  { id: 'status', label: 'Status', alignRight: false, sortable: true },
  { id: 'details', label: 'Details', alignRight: false, sortable: false }
];

// Utility functions
const getStatusColor = (status) => {
  switch (status) {
    case 'success': return 'success';
    case 'error': return 'error';
    case 'warning': return 'warning';
    default: return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'success': return checkmarkCircle2Fill;
    case 'error': return alertTriangleFill;
    case 'warning': return alertTriangleFill;
    default: return clockFill;
  }
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'Authentication': return shieldFill;
    case 'User Management': return personFill;
    case 'Inventory': return activityFill;
    case 'System': return clockFill;
    default: return activityFill;
  }
};

const getRelativeTime = (timestamp) => {
  return moment(timestamp).fromNow();
};

const formatTimestamp = (timestamp) => {
  const date = moment(timestamp);
  return {
    date: date.format('MMM DD, YYYY'),
    time: date.format('HH:mm:ss'),
    relative: getRelativeTime(timestamp)
  };
};

// Enhanced sorting function
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Export functions
const exportToCSV = (logs, filename = 'activity-logs') => {
  const headers = ['Timestamp', 'User', 'Role', 'Action', 'Category', 'Status', 'Details', 'IP Address'];
  const csvContent = [
    headers.join(','),
    ...logs.map(log => [
      log.timestamp,
      log.user,
      log.role,
      log.action,
      log.category,
      log.status,
      `"${log.details.replace(/"/g, '""')}"`, // Escape quotes in details
      log.ipAddress
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}-${moment().format('YYYY-MM-DD')}.csv`);
};

const exportToJSON = (logs, filename = 'activity-logs') => {
  const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
  saveAs(blob, `${filename}-${moment().format('YYYY-MM-DD')}.json`);
};

// Analytics calculation
const calculateAnalytics = (logs) => {
  const today = moment().startOf('day');
  const thisWeek = moment().startOf('week');
  
  const todayLogs = logs.filter(log => moment(log.timestamp).isAfter(today));
  const weekLogs = logs.filter(log => moment(log.timestamp).isAfter(thisWeek));
  
  const failedLogins = logs.filter(log => 
    log.action.toLowerCase().includes('failed') || log.status === 'error'
  ).length;
  
  const userCounts = logs.reduce((acc, log) => {
    acc[log.user] = (acc[log.user] || 0) + 1;
    return acc;
  }, {});
  
  const mostActiveUser = Object.entries(userCounts).reduce((a, b) => 
    userCounts[a[0]] > userCounts[b[0]] ? a : b, ['', 0]
  );
  
  const actionCounts = logs.reduce((acc, log) => {
    acc[log.action] = (acc[log.action] || 0) + 1;
    return acc;
  }, {});
  
  const mostCommonAction = Object.entries(actionCounts).reduce((a, b) => 
    actionCounts[a[0]] > actionCounts[b[0]] ? a : b, ['', 0]
  );

  return {
    totalActions: logs.length,
    todayActions: todayLogs.length,
    weekActions: weekLogs.length,
    failedAttempts: failedLogins,
    mostActiveUser: mostActiveUser[0],
    mostCommonAction: mostCommonAction[0]
  };
};

// Enhanced Table Head Component
function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {TABLE_HEAD.map((headCell) => {
          // Hide certain columns on mobile
          if (isMobile && (headCell.id === 'role' || headCell.id === 'category' || headCell.id === 'details')) {
            return null;
          }
          
          return (
            <TableCell
              key={headCell.id}
              align={headCell.alignRight ? 'right' : 'left'}
              sortDirection={orderBy === headCell.id ? order : false}
              sx={{ 
                fontWeight: 'bold', 
                backgroundColor: 'background.neutral',
                minWidth: isMobile && headCell.id === 'timestamp' ? 120 : 
                         isMobile && headCell.id === 'user' ? 100 :
                         isMobile && headCell.id === 'action' ? 120 : 'auto'
              }}
            >
              {headCell.sortable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

// Analytics Cards Component
function AnalyticsCards({ analytics, loading }) {
  const analyticsCards = [
    {
      title: 'Total Activities',
      value: analytics.totalActions,
      icon: activityFill,
      color: 'primary'
    },
    {
      title: "Today's Activities",
      value: analytics.todayActions,
      icon: calendarFill,
      color: 'info'
    },
    {
      title: 'This Week',
      value: analytics.weekActions,
      icon: clockFill,
      color: 'success'
    },
    {
      title: 'Failed Attempts',
      value: analytics.failedAttempts,
      icon: alertTriangleFill,
      color: 'error'
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {analyticsCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    {card.title}
                  </Typography>
                  <Typography variant="h4">
                    {loading ? <Skeleton width={40} /> : card.value}
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    backgroundColor: `${card.color}.main`,
                    height: 56,
                    width: 56
                  }}
                >
                  <Icon icon={card.icon} width={24} height={24} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// Details Modal Component
function DetailsModal({ open, onClose, logDetails }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!logDetails) return null;

  const timestamp = formatTimestamp(logDetails.timestamp);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="log-details-modal"
      closeAfterTransition
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : '600px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 0,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="h2">
            Activity Details
          </Typography>
          <IconButton 
            onClick={onClose} 
            sx={{ color: 'primary.contrastText' }}
            size="small"
          >
            <Icon icon={closeFill} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
          <Grid container spacing={3}>
            {/* Timestamp */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Icon icon={clockFill} />
                <Typography variant="subtitle2" color="text.secondary">
                  Timestamp
                </Typography>
              </Box>
              <Typography variant="body1">
                {timestamp.date} at {timestamp.time}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {timestamp.relative}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* User */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Icon icon={personFill} />
                <Typography variant="subtitle2" color="text.secondary">
                  User
                </Typography>
              </Box>
              <Typography variant="body1">
                {logDetails.user?.displayName || logDetails.userDisplay?.name || logDetails.user?.email || 'Unknown User'}
              </Typography>
              {logDetails.user?.email && logDetails.user?.email !== logDetails.user?.displayName && (
                <Typography variant="caption" color="text.secondary">
                  {logDetails.user.email}
                </Typography>
              )}
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Icon icon={shieldFill} />
                <Typography variant="subtitle2" color="text.secondary">
                  Role
                </Typography>
              </Box>
              <Chip label={logDetails.role} size="small" variant="outlined" />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Activity */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Icon icon={getCategoryIcon(logDetails.category)} />
                <Typography variant="subtitle2" color="text.secondary">
                  Activity
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {logDetails.action}
              </Typography>
              <Chip 
                label={logDetails.category} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Icon icon={getStatusIcon(logDetails.status)} />
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
              </Box>
              <Chip
                label={logDetails.status}
                color={getStatusColor(logDetails.status)}
                size="small"
                icon={<Icon icon={getStatusIcon(logDetails.status)} />}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Details */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Icon icon={activityFill} />
                <Typography variant="subtitle2" color="text.secondary">
                  Details
                </Typography>
              </Box>
              <Paper 
                variant="outlined" 
                sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {logDetails.details || 'No additional details available'}
                </Typography>
              </Paper>
            </Grid>

            {/* IP Address (if available) */}
            {logDetails.ipAddress && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Icon icon={activityFill} />
                    <Typography variant="subtitle2" color="text.secondary">
                      IP Address
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {logDetails.ipAddress}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.neutral', 
          display: 'flex', 
          justifyContent: 'flex-end' 
        }}>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

// Date Range Picker Component
function CustomDateRangePicker({ value, onChange, error, helperText }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formatDateDisplay = (date) => {
    return date ? moment(date).format('MMM DD, YYYY') : '';
  };

  const getDisplayText = () => {
    if (value.start && value.end) {
      const startFormatted = isMobile ? moment(value.start).format('MM/DD') : formatDateDisplay(value.start);
      const endFormatted = isMobile ? moment(value.end).format('MM/DD') : formatDateDisplay(value.end);
      return `${startFormatted} - ${endFormatted}`;
    } else if (value.start) {
      const startFormatted = isMobile ? moment(value.start).format('MM/DD') : formatDateDisplay(value.start);
      return `From ${startFormatted}`;
    } else if (value.end) {
      const endFormatted = isMobile ? moment(value.end).format('MM/DD') : formatDateDisplay(value.end);
      return `Until ${endFormatted}`;
    }
    return 'Select date range';
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRangeChange = (ranges) => {
    const range = ranges.selection;
    let startDate = range.startDate;
    let endDate = range.endDate;
    
    // If only one date is selected (start and end are the same), 
    // set end date to end of day to capture full day's data
    if (startDate && endDate && moment(startDate).isSame(moment(endDate), 'day')) {
      startDate = moment(startDate).startOf('day').toDate();
      endDate = moment(endDate).endOf('day').toDate();
    }
    
    onChange({
      start: startDate,
      end: endDate
    });
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ start: null, end: null });
    handleClose();
  };

  const open = Boolean(anchorEl);
  const hasValue = value.start || value.end;

  // Convert our date range format to react-date-range format
  const selectionRange = {
    startDate: value.start || moment().startOf('day').toDate(),
    endDate: value.end || moment().endOf('day').toDate(),
    key: 'selection',
  };

  // Predefined ranges for quick selection
  const definedRanges = [
    {
      label: 'Today',
      range: () => ({
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      }),
      isSelected: () => {
        const today = moment().startOf('day');
        const todayEnd = moment().endOf('day');
        return value.start && value.end && 
               moment(value.start).isSame(today, 'day') && 
               moment(value.end).isSame(todayEnd, 'day');
      }
    },
    {
      label: 'Yesterday',
      range: () => ({
        startDate: moment().subtract(1, 'day').startOf('day').toDate(),
        endDate: moment().subtract(1, 'day').endOf('day').toDate()
      }),
      isSelected: () => {
        const yesterday = moment().subtract(1, 'day').startOf('day');
        const yesterdayEnd = moment().subtract(1, 'day').endOf('day');
        return value.start && value.end && 
               moment(value.start).isSame(yesterday, 'day') && 
               moment(value.end).isSame(yesterdayEnd, 'day');
      }
    },
    {
      label: 'Last 7 Days',
      range: () => ({
        startDate: moment().subtract(7, 'days').startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      }),
      isSelected: () => {
        const start = moment().subtract(7, 'days').startOf('day');
        const end = moment().endOf('day');
        return value.start && value.end && 
               moment(value.start).isSame(start, 'day') && 
               moment(value.end).isSame(end, 'day');
      }
    },
    {
      label: 'Last 30 Days',
      range: () => ({
        startDate: moment().subtract(30, 'days').startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      }),
      isSelected: () => {
        const start = moment().subtract(30, 'days').startOf('day');
        const end = moment().endOf('day');
        return value.start && value.end && 
               moment(value.start).isSame(start, 'day') && 
               moment(value.end).isSame(end, 'day');
      }
    },
    {
      label: isMobile ? 'This Mo.' : 'This Month',
      range: () => ({
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate()
      }),
      isSelected: () => {
        const start = moment().startOf('month');
        const end = moment().endOf('month');
        return value.start && value.end && 
               moment(value.start).isSame(start, 'day') && 
               moment(value.end).isSame(end, 'day');
      }
    },
    {
      label: isMobile ? 'Last Mo.' : 'Last Month',
      range: () => ({
        startDate: moment().subtract(1, 'month').startOf('month').toDate(),
        endDate: moment().subtract(1, 'month').endOf('month').toDate()
      }),
      isSelected: () => {
        const start = moment().subtract(1, 'month').startOf('month');
        const end = moment().subtract(1, 'month').endOf('month');
        return value.start && value.end && 
               moment(value.start).isSame(start, 'day') && 
               moment(value.end).isSame(end, 'day');
      }
    }
  ];

  return (
    <>
      <TextField
        fullWidth
        label="Date Range"
        value={getDisplayText()}
        onClick={handleClick}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon={calendarFill} />
            </InputAdornment>
          ),
          endAdornment: hasValue && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear}>
                <Icon icon={closeFill} />
              </IconButton>
            </InputAdornment>
          )
        }}
        error={error}
        helperText={helperText}
        sx={{
          cursor: 'pointer',
          '& .MuiInputBase-input': {
            cursor: 'pointer'
          }
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'center' : 'left',
        }}
        PaperProps={{
          sx: { 
            p: 0,
            maxWidth: isMobile ? '95vw' : 'none',
            maxHeight: isMobile ? '80vh' : 'none',
            '& .rdrDefinedRangesWrapper': {
              fontSize: '14px',
              borderRight: '1px solid #e0e0e0',
              width: isMobile ? '100px' : 'auto'
            },
            '& .rdrCalendarWrapper': {
              fontSize: '14px'
            },
            '& .rdrDateRangePickerWrapper': {
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row'
            },
            '& .rdrDefinedRangesWrapper .rdrStaticRange': {
              fontSize: isMobile ? '12px' : '14px',
              padding: isMobile ? '4px 8px' : '8px 12px'
            }
          }
        }}
      >
        <DateRangePicker
          ranges={[selectionRange]}
          onChange={handleRangeChange}
          definedRanges={definedRanges}
          showSelectionPreview={true}
          moveRangeOnFirstSelection={false}
          months={isMobile ? 1 : 2}
          direction={isMobile ? 'vertical' : 'horizontal'}
          maxDate={moment().add(1, 'day').toDate()}
          rangeColors={['#1976d2']}
          showDateDisplay={false}
        />
        
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid #e0e0e0', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 1 
        }}>
          <Button onClick={handleClose} size="small" fullWidth={isMobile}>
            Close
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default function AdminActionLogs() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Sorting state
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('timestamp');
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [dateRange, setDateRange] = useState({ 
    start: moment().startOf('day').toDate(), 
    end: moment().endOf('day').toDate() 
  });
  
  // UI state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedLogDetails, setSelectedLogDetails] = useState(null);

  // Client-side validation helper
  const validateParams = useCallback(() => {
    // Validate search term length
    if (searchTerm && searchTerm.length > 200) {
      enqueueSnackbar('Search term cannot exceed 200 characters', { variant: 'warning' });
      return false;
    }

    // Validate action filter length
    if (actionFilter && actionFilter.length > 100) {
      enqueueSnackbar('Action filter cannot exceed 100 characters', { variant: 'warning' });
      return false;
    }

    // Validate date range
    if (dateRange.start && dateRange.end && dateRange.start > dateRange.end) {
      enqueueSnackbar('Start date cannot be after end date', { variant: 'warning' });
      return false;
    }

    // Validate dates are not too far in the future
    const now = new Date();
    const maxFutureDate = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1 day from now

    if (dateRange.start && dateRange.start > maxFutureDate) {
      enqueueSnackbar('Start date cannot be more than 1 day in the future', { variant: 'warning' });
      return false;
    }

    if (dateRange.end && dateRange.end > maxFutureDate) {
      enqueueSnackbar('End date cannot be more than 1 day in the future', { variant: 'warning' });
      return false;
    }

    // Validate pagination
    if (page < 0) {
      enqueueSnackbar('Page number cannot be negative', { variant: 'warning' });
      return false;
    }

    if (rowsPerPage < 1 || rowsPerPage > 100) {
      enqueueSnackbar('Rows per page must be between 1 and 100', { variant: 'warning' });
      return false;
    }

    return true;
  }, [searchTerm, actionFilter, dateRange, page, rowsPerPage, enqueueSnackbar]);

  // Fetch logs from the backend
  const fetchLogs = useCallback(async () => {
    // Always fetch data based on date range, but require date range to be set
    if (!dateRange.start || !dateRange.end) {
      return;
    }

    if (selectedCompany === '') {
      return;
    }

    setLoading(true);
    setError(null);

    // Perform client-side validation first
    if (!validateParams()) {
      setLoading(false);
      return;
    }

    try {
      const params = {
        page,
        rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
        searchTerm,
        role: roleFilter,
        action: actionFilter,
        category: categoryFilter,
        status: statusFilter,
        company: selectedCompany || currentUser?.company,
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString()
      };

      const response = await usersAPI.get_action_logs(params);
      
      setLogs(response.data.data || []);
      setTotalLogs(response.data.total || 0);

    } catch (error) {
      console.error('Failed to fetch action logs', error);
      
      // Handle specific validation errors from backend
      let errorMessage = 'Failed to load action logs. Please try again.';
      
      if (error.response && error.response.data && error.response.data.msg) {
        errorMessage = error.response.data.msg;
        
        // Show specific validation error in snackbar
        enqueueSnackbar(errorMessage, { variant: 'error' });
        
        // For validation errors (400), don't set the general error state
        // as the user can fix the issue by adjusting filters
        if (error.response.status === 400) {
          setError(null);
          setLoading(false);
          return;
        }
      } else if (error.response && error.response.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setLogs([]);
      setTotalLogs(0);
    }
    setLoading(false);
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    searchTerm,
    roleFilter,
    actionFilter,
    categoryFilter,
    statusFilter,
    dateRange,
    enqueueSnackbar,
    validateParams,
    selectedCompany
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    const loadUserData = async () => {
      const userStr = await storage.getUser();
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        setSelectedCompany(user.company);
        if (user.role >= 3) {
          try {
            const storesRes = await usersAPI.get_group_store(user._id);
            if (storesRes && storesRes.status === 200 && storesRes.data?.report?.store) {
              const stores = storesRes.data.report.store;
              const sortedStores = stores
                .slice()
                .sort((a, b) => a.trim().replace(/\s+/g, ' ').localeCompare(b.trim().replace(/\s+/g, ' ')));
              setCompanies([...sortedStores, user.company]);
            }
          } catch (error) {
            console.error('Failed to fetch companies', error);
            enqueueSnackbar('Failed to fetch company list', { variant: 'error' });
          }
        } else {
          setCompanies([]);
        }
      }
    };
    loadUserData();
  }, [enqueueSnackbar]);

  // Since filtering and sorting are handled by the backend, we use logs directly
  const displayLogs = logs;

  // Analytics
  const analytics = useMemo(() => calculateAnalytics(logs), [logs]);

  // Get unique values for filters
  const uniqueRoles = [...new Set(logs.map(log => log.role))];
  const uniqueActions = [...new Set(logs.map(log => log.action))];
  const uniqueCategories = [...new Set(logs.map(log => log.category))];
  const uniqueStatuses = [...new Set(logs.map(log => log.status))];

  // Event handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportClick = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = (format) => {
    const exportData = displayLogs;
    if (format === 'csv') {
      exportToCSV(exportData);
    } else if (format === 'json') {
      exportToJSON(exportData);
    }
    handleExportClose();
    enqueueSnackbar(`Logs exported as ${format.toUpperCase()}`, { variant: 'success' });
  };

  const handleClearFilters = () => {
    setRoleFilter('');
    setActionFilter('');
    setCategoryFilter('');
    setStatusFilter('');
  };

  const handleDetailsClick = (log) => {
    setSelectedLogDetails(log);
    setDetailsModalOpen(true);
  };

  const handleDetailsModalClose = () => {
    setDetailsModalOpen(false);
    setSelectedLogDetails(null);
  };

  const hasActiveFilters = roleFilter || actionFilter || categoryFilter || statusFilter

  return (
    <Page title="Admin Activity Logs | Sparkle TimeIn">
      <Container 
        maxWidth="xl"
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 3
        }}
      >
        {/* Header */}
        <Box sx={{ pb: 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
            Activity Logs
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              fontSize: isMobile ? '0.875rem' : '1rem',
              lineHeight: isMobile ? 1.4 : 1.5
            }}
          >
            A comprehensive record of all activities taken by administrators and store managers. These logs are immutable and provide a complete audit trail.
          </Typography>
        </Box>

        {/* Analytics Cards */}
        <AnalyticsCards analytics={analytics} loading={loading} />

        {/* Details Modal */}
        <DetailsModal
          open={detailsModalOpen}
          onClose={handleDetailsModalClose}
          logDetails={selectedLogDetails}
        />

        {/* Main Content Card */}
        <Card>
          <CardContent>
            {/* Toolbar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'stretch' : 'center', 
                justifyContent: 'space-between', 
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: 'wrap', 
                gap: 2 
              }}>
                {/* Search and Date Range */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: 2, 
                  flexWrap: 'wrap',
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <TextField
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    inputProps={{ 
                      maxLength: 200 
                    }}
                    helperText={searchTerm.length > 180 ? `${200 - searchTerm.length} characters remaining` : ''}
                    error={searchTerm.length > 200}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon icon={searchFill} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ 
                      minWidth: isMobile ? '100%' : 300,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  />
                  
                  <Box sx={{ 
                    minWidth: isMobile ? '100%' : 250,
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <CustomDateRangePicker
                      value={dateRange}
                      onChange={setDateRange}
                      error={dateRange.start && dateRange.end && dateRange.start > dateRange.end}
                      helperText={
                        dateRange.start && dateRange.end && dateRange.start > dateRange.end 
                          ? "Invalid date range" 
                          : ""
                      }
                    />
                  </Box>

                  {/* Company Selector */}
                  {currentUser && currentUser.role >= 3 && companies.length > 0 && (
                    <Box sx={{ 
                      minWidth: isMobile ? '100%' : 250,
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      <Autocomplete
                        value={selectedCompany || null}
                        onChange={(event, newValue) => {
                          setSelectedCompany(newValue || '');
                        }}
                        options={['', ...companies]}
                        getOptionLabel={(option) => option === '' ? currentUser?.company : option}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Company"
                            placeholder="Select company..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon icon={personFill} />
                                </InputAdornment>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            {option === '' ? (
                              <Typography variant="body2" color="textSecondary" fontStyle="italic">
                                Select Company
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {option}
                              </Typography>
                            )}
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        clearOnBlur
                        selectOnFocus
                        handleHomeEndKeys
                      />
                    </Box>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: isMobile ? 'center' : 'flex-start'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<Icon icon={filterFill} />}
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    color={hasActiveFilters ? 'primary' : 'inherit'}
                    size={isMobile ? 'medium' : 'medium'}
                    sx={{ flex: isMobile ? 1 : 'none' }}
                  >
                    Filters
                    {hasActiveFilters && (
                      <Badge badgeContent={1} color="primary" sx={{ ml: 1 }}>
                        <Box />
                      </Badge>
                    )}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<Icon icon={downloadFill} />}
                    onClick={handleExportClick}
                    disabled={displayLogs.length === 0}
                    size={isMobile ? 'medium' : 'medium'}
                    sx={{ flex: isMobile ? 1 : 'none' }}
                  >
                    Export
                  </Button>
                  
                  <IconButton 
                    onClick={fetchLogs} 
                    disabled={loading}
                    size={isMobile ? 'medium' : 'medium'}
                  >
                    <Icon icon={refreshFill} />
                  </IconButton>
                </Box>
              </Box>

              {/* Filters Panel */}
              <Collapse in={filtersOpen}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={roleFilter}
                          label="Role"
                          onChange={(e) => setRoleFilter(e.target.value)}
                        >
                          <MenuItem value="">All Roles</MenuItem>
                          {uniqueRoles.map(role => (
                            <MenuItem key={role} value={role}>{role}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Activity</InputLabel>
                        <Select
                          value={actionFilter}
                          label="Activity"
                          onChange={(e) => setActionFilter(e.target.value)}
                          error={actionFilter.length > 100}
                        >
                          <MenuItem value="">All Activities</MenuItem>
                          {uniqueActions.map(action => (
                            <MenuItem key={action} value={action}>{action}</MenuItem>
                          ))}
                        </Select>
                        {actionFilter.length > 80 && (
                          <Typography variant="caption" color={actionFilter.length > 100 ? 'error' : 'textSecondary'}>
                            {100 - actionFilter.length} characters remaining
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={categoryFilter}
                          label="Category"
                          onChange={(e) => setCategoryFilter(e.target.value)}
                        >  
                          <MenuItem value="">All Categories</MenuItem>
                          {uniqueCategories.map(category => (
                            <MenuItem key={category} value={category}>{category}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          label="Status"
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <MenuItem value="">All Statuses</MenuItem>
                          {uniqueStatuses.map(status => (
                            <MenuItem key={status} value={status}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon icon={getStatusIcon(status)} />
                                {status}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ 
                    mt: 2, 
                    display: 'flex', 
                    justifyContent: isMobile ? 'center' : 'flex-end' 
                  }}>
                    <Button
                      variant="outlined"
                      onClick={handleClearFilters}
                      disabled={!hasActiveFilters}
                      size="small"
                      startIcon={<Icon icon={closeFill} />}
                      fullWidth={isMobile}
                    >
                      Clear All Filters
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Results Summary */}
            {!loading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {displayLogs.length} of {totalLogs} logs
                  {hasActiveFilters && ` (with filters applied)`}
                </Typography>
              </Box>
            )}

            {/* Table */}
            {loading ? (
              <Box sx={{ py: 3 }}>
                {[...Array(5)].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {[...Array(isMobile ? 3 : 8)].map((_, cellIndex) => (
                      <Skeleton key={cellIndex} height={40} sx={{ flex: 1 }} />
                    ))}
                  </Box>
                ))}
              </Box>
            ) : (
              <Scrollbar>
                <TableContainer sx={{ 
                  minWidth: isMobile ? 350 : 1200,
                  maxHeight: isMobile ? '70vh' : 'none'
                }}>
                  <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {displayLogs.length > 0 ? (
                        displayLogs.map((log) => {
                          const timestamp = formatTimestamp(log.timestamp);
                          return (
                            <TableRow key={log.id} hover>
                              <TableCell sx={{ minWidth: isMobile ? 120 : 'auto' }}>
                                <Box>
                                  <Typography variant={isMobile ? 'caption' : 'body2'}>
                                    {isMobile ? moment(log.timestamp).format('MM/DD') : timestamp.date}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {isMobile ? moment(log.timestamp).format('HH:mm') : `${timestamp.time} (${timestamp.relative})`}
                                  </Typography>
                                </Box>
                              </TableCell>
                              
                              <TableCell sx={{ minWidth: isMobile ? 100 : 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant={isMobile ? 'caption' : 'body2'} noWrap>
                                    {log.user?.displayName || log.userDisplay?.name || log.user?.email || 'Unknown User'}
                                  </Typography>
                                  {!isMobile && log.user?.email && log.user?.email !== log.user?.displayName && (
                                    <Typography variant="caption" color="text.secondary">
                                      {log.user.email}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              
                              {!isMobile && (
                                <TableCell>
                                  <Chip label={log.role} size="small" variant="outlined" />
                                </TableCell>
                              )}
                              
                              <TableCell sx={{ minWidth: isMobile ? 120 : 'auto' }}>
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    cursor: isMobile ? 'pointer' : 'default',
                                    '&:hover': isMobile ? {
                                      backgroundColor: 'action.hover',
                                      borderRadius: 1,
                                      padding: '4px 8px',
                                      margin: '-4px -8px'
                                    } : {}
                                  }}
                                  onClick={isMobile ? () => handleDetailsClick(log) : undefined}
                                >
                                  <Icon icon={getCategoryIcon(log.category)} />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant={isMobile ? 'caption' : 'body2'} noWrap>
                                      {isMobile ? log.action.substring(0, 20) + (log.action.length > 20 ? '...' : '') : log.action}
                                    </Typography>
                                    {isMobile && (
                                      <Typography variant="caption" color="text.secondary">
                                        Tap for details
                                      </Typography>
                                    )}
                                  </Box>
                                  {isMobile && (
                                    <Icon icon={arrowUpFill} style={{ transform: 'rotate(90deg)' }} />
                                  )}
                                </Box>
                              </TableCell>
                              
                              {!isMobile && (
                                <TableCell>
                                  <Chip 
                                    label={log.category} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined" 
                                  />
                                </TableCell>
                              )}
                              
                              <TableCell>
                                <Chip
                                  label={log.status}
                                  color={getStatusColor(log.status)}
                                  size="small"
                                  icon={<Icon icon={getStatusIcon(log.status)} />}
                                />
                              </TableCell>
                              
                              {!isMobile && (
                                <TableCell>
                                  <Typography variant="body2">
                                    {log.details}
                                  </Typography>
                                </TableCell>
                              )}
                              
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 8 }}>
                            <Typography variant="h6" color="text.secondary">
                              {hasActiveFilters ? 'No logs match your filters' : 'No activity logs available'}
                            </Typography>
                            {hasActiveFilters && (
                              <Button onClick={handleClearFilters} sx={{ mt: 1 }}>
                                Clear filters
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
            )}

            {/* Pagination */}
            {!loading && logs.length > 0 && (
              <TablePagination
                rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
                component="div"
                count={totalLogs}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={isMobile ? "" : "Rows per page:"}
                labelDisplayedRows={({ from, to, count }) => 
                  isMobile ? `${from}-${to}` : `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'center' : 'flex-end',
                    gap: isMobile ? 1 : 0
                  },
                  '& .MuiTablePagination-selectLabel': {
                    display: isMobile ? 'none' : 'block'
                  },
                  '& .MuiTablePagination-input': {
                    marginLeft: isMobile ? 0 : 1
                  }
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Export Menu */}
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportClose}
        >
          <MenuItem onClick={() => handleExport('csv')}>
            <ListItemIcon>
              <Icon icon={downloadFill} />
            </ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExport('json')}>
            <ListItemIcon>
              <Icon icon={downloadFill} />
            </ListItemIcon>
            <ListItemText>Export as JSON</ListItemText>
          </MenuItem>
        </Menu>
      </Container>
    </Page>
  );
}
